import React, { useState, useMemo, createContext, useEffect } from 'react';
import { AuthProvider, useAuth } from '../components/AuthContext';
import { Toaster } from '../components/ui/sonner';
import { SignIn } from '../components/SignIn';
import { ResetPassword } from '../components/ResetPassword';
import { Profile } from '../components/Profile';
import { Layout } from '../components/Layout';
import { MetricCard } from '../components/MetricCard';
import { DateRangeFilter, DateRange } from '../components/DateRangeFilter';
import { HarvestRunTable } from '../components/HarvestRunTable';
import { AIPromptManager } from '../components/AIPromptManager';
import { BatchApproval } from '../components/BatchApproval';
import { Settings } from '../components/Settings';
import { AuditGoalsDetailView } from '../components/AuditGoalsDetailView';
import { PromptDetailView } from '../components/PromptDetailView';
import { Reports } from '../components/Reports';
import { UserManagement } from '../components/UserManagement';
import { AdminAuditDashboard } from '../components/AdminAuditDashboard';
import { SystemActivityLog } from '../components/SystemActivityLog';
import { UserActivityTracker } from '../components/UserActivityTracker';
import { SecurityAuditLog } from '../components/SecurityAuditLog';
import { PerformanceMonitor } from '../components/PerformanceMonitor';
import { ConfigurationAudit } from '../components/ConfigurationAudit';
import { ChatHarvest } from '../components/ChatHarvest';
import { HarvestRunView } from '../components/HarvestRunView';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '../components/ui/breadcrumb';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { CheckCircle, Clock, Zap, Home, TrendingUp, Users, Activity, Calendar } from 'lucide-react';
import { format, isWithinInterval, startOfDay, endOfDay, differenceInDays, subDays } from 'date-fns';
import { apiClient } from './Utils/apiClient';
import { HarvestRunLogView } from '../components/HarvestRunLogView';
import { AuditDecisionComparison } from '../components/AuditDecisionComparison';
import { BatchFileManager } from '../components/BatchFileManager';
import { BatchFileDetailView } from '../components/BatchFileDetailView';
import { TurfAIStudio } from '../components/TurfAIStudio';
import { mapSideMenu } from './Utils/helpers';
import { ChartExtractionRunDetail } from '../components/ChartExtractionRunDetail';
import { ChartExtractionRuns } from '../components/ChartExtractionRuns';
import DashboardView from '../components/DashboardView';
import { KanTimeUpdate } from '../components/KanTimeUpdate';
import { KanTimeDetails } from '../components/KanTimeDetails';
import { KanTimeRunLogView } from '../components/KanTimeRunLogView';
import { SystemAuditLog } from '../components/systemAuditLog/SystemAuditLog';
import { BackUpManagement } from '../components/BackUpManagement';
import { ResetPasswordPage } from '../components/ResetPasswordPage';
import { ClientClinicianView } from '../components/ClientClinicianView';
import { MagicLinkHandler } from '../components/MagicLinkHandler';
import { RenewalTasksDashboard } from '../components/RenewalTasksDashboard';
import { SupervisorRiskView } from '../components/SupervisorRiskView';
import { SupervisorDashboard } from '../components/SupervisorDashboard';
import { StaffTasksView } from '../components/StaffTasksView';
import { ClientDetailsView } from '../components/ClientDetailsView';
import { WorkflowRunLogsView } from '../components/WorkflowRunLogsView';
import { Routes, Route, Navigate, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { PATH_TO_VIEW, ROUTES } from './routes/config';

//  for value that show in as per env 
// import {demoLabels} from './Utils/labels/demoLabels';
// const orgConfigDetails = demoLabels;
//  for custom map labels
import demoLabels from './Utils/labels/demoLabels';
import { RNChartDetailView } from '../components/RNChartDetailView';
const activeOrg = import.meta.env.VITE_ORG as keyof typeof demoLabels.org || "kanTime";
const orgConfigDetails = demoLabels.org[activeOrg];
// App state management
export type AppView = 'dashboard' | 'ai-prompt-manager' | 'prompt-detail' | 'batch-approval' | 'settings' | 'audit-goals-detail' | 'reports' | 'user-management' | 'help' | 'admin-audit-dashboard' | 'system-activity-log' | 'user-activity-tracker' | 'security-audit-log' | 'performance-monitor' | 'configuration-audit' | 'profile' | 'chat-harvest' | 'harvest-run-detail' | 'batch-run-log-view' | 'audit-decision-comparison' | 'batch-file-manager' | 'batch-file-detail' | 'turf-ai-studio' | 'chart-extraction-run-detail' | 'chart-extraction-runs' | 'kan-time-update' | 'kanTime-details' | 'kanTime-runLog' | 'backUp-management' | 'audit-logs' | 'sn-chart-detail' | 'client-clinician-view' | '';

interface AppState {
  currentView: AppView;
  selectedAuditData?: any;
  selectedPromptData?: any;
  selectedHarvestRunData?: any;
  selectedBatchFileData?: any;
  selectedExtractionRunData?: any;
}
const initialChartIndexState = {
  index: 0,
  filterItems: [],
  totalChart: 0,
  filters: {} as Record<string, any> // dynamic keys
};
const paginationFields = {
  pageNo: 1
}
// labels
type OrgType = {
  name: string;
  logo: string;
  banner: string;
  labels: {
    status: string;
    updated: string;
    pending: string;
    default: string;
  };
  managedBy: {
    name: string;
    logo: string;
  };
};


// Auth views
type AuthView = 'signin' | 'reset-password';

// Generate mock healthcare audit data
const generateMockAuditData = () => {
  const data = [];
  const startDate = new Date('2023-01-01');
  const endDate = new Date('2024-12-31');

  const clientIdPrefix = ['PT', 'MR', 'ID'];
  const clinicianIdPrefix = ['DR', 'NP', 'CL'];
  const auditorNames = ['John Smith', 'Sarah Johnson', 'Mike Chen', 'Lisa Davis', 'Dr. Brown'];
  const goalSummaries = [
    'Diabetes management and glucose monitoring',
    'Hypertension control and medication adherence',
    'Post-operative wound care and infection prevention',
    'Mental health assessment and therapy recommendations',
    'Cardiac rehabilitation and lifestyle modifications',
    'Pain management and opioid stewardship',
    'Preventive care screening and immunizations',
    'Chronic kidney disease monitoring',
    'Asthma management and inhaler technique',
    'Weight management and nutritional counseling'
  ];
  const aiSuggestions = [
    'Recommend HbA1c testing every 3 months',
    'Suggest medication dosage adjustment',
    'Schedule follow-up appointment in 2 weeks',
    'Refer to specialist for further evaluation',
    'Implement care plan modifications',
    'Monitor vital signs more frequently',
    'Initiate client education program',
    'Consider alternative treatment options',
    'Update medication reconciliation',
    'Coordinate with interdisciplinary team'
  ];

  let sNo = 1;

  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    const dayOfYear = Math.floor((d.getTime() - new Date(d.getFullYear(), 0, 0).getTime()) / (1000 * 60 * 60 * 24));
    const baseAudits = Math.floor(Math.random() * 5) + 1; // 1-5 audits per day
    const seasonalFactor = 1 + 0.3 * Math.sin((dayOfYear / 365) * 2 * Math.PI);

    const auditsCount = Math.max(1, Math.floor(baseAudits * seasonalFactor));

    for (let i = 0; i < auditsCount; i++) {
      const outcomes = ['Approved', 'Rejected', 'Skipped'];
      const weights = [0.6, 0.25, 0.15];
      const randomValue = Math.random();
      let outcome;

      if (randomValue < (weights[0] || 0)) outcome = 'Approved';
      else if (randomValue < ((weights[0] || 0) + (weights[1] || 0))) outcome = 'Rejected';
      else outcome = 'Skipped';

      const isAiAssisted = d >= new Date('2023-06-01') && Math.random() < 0.75;

      const conformanceStatuses = ['Compliant', 'Non-Compliant', 'Under Review'];
      const statusWeights = [0.7, 0.2, 0.1];
      const statusRandom = Math.random();
      let conformanceStatus;

      if (statusRandom < (statusWeights[0] || 0)) conformanceStatus = 'Compliant';
      else if (statusRandom < ((statusWeights[0] || 0) + (statusWeights[1] || 0))) conformanceStatus = 'Non-Compliant';
      else conformanceStatus = 'Under Review';

      // Add supervisor-specific fields
      const needsReview = Math.random() < 0.15; // 15% need supervisor review
      const isOverridden = Math.random() < 0.05; // 5% have been overridden
      const auditorName = auditorNames[Math.floor(Math.random() * auditorNames.length)];

      data.push({
        sNo: sNo++,
        date: new Date(d),
        outcome,
        reviewTime: Math.random() * 8 + 1,
        isAiAssisted,
        estimatedManualTime: (Math.random() * 8 + 1) * 2.5,
        clientId: `${clientIdPrefix[Math.floor(Math.random() * clientIdPrefix.length)]}${String(Math.floor(Math.random() * 90000) + 10000)}`,
        clinicianId: `${clinicianIdPrefix[Math.floor(Math.random() * clinicianIdPrefix.length)]}${String(Math.floor(Math.random() * 9000) + 1000)}`,
        visitDate: format(d, 'MMM dd, yyyy'),
        goalSummary: goalSummaries[Math.floor(Math.random() * goalSummaries.length)],
        aiSuggestion: aiSuggestions[Math.floor(Math.random() * aiSuggestions.length)],
        confidence: Math.floor(Math.random() * 40) + 60, // 60-100% confidence
        conformanceStatus,
        // Supervisor fields
        auditorName,
        needsReview,
        isOverridden,
        supervisorComment: isOverridden ? 'Documentation standards require clarification on treatment plan progression.' : undefined,
        flaggedReason: needsReview ? ['Low confidence score', 'Complex case', 'Policy exception'][Math.floor(Math.random() * 3)] : undefined,
      });
    }
  }

  return data;
};

const allAuditData = generateMockAuditData();

// Create a context for app navigation
export const AppContext = createContext<{
  currentView: AppView;
  selectedAuditData?: any;
  selectedPromptData?: any;
  selectedHarvestRunData?: any;
  currentPageHarvestRun?: any
  isCollapsed: boolean;
  selectedBatchFileData: any,
  sideBarItems: any;
  orgConfig: any;
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  selectedExtractionRunData?: any;
  currentIndexOfListedChart?: any;
  currentIndexOfClinicianChart?: any;
  previousViewScreen?: String;
  pagination?: any;
  setSelectedExtractionRunData: (data?: any) => void;
  setCurrentView: (view: AppView) => void;
  setSelectedAuditData: (data?: any) => void;
  setSelectedPromptData: (data?: any) => void;
  setSelectedHarvestRunData: (data?: any) => void;
  setCurrentPageHarvestRuns: (data?: any) => void;
  setSelectedBatchFileData: (data?: any) => void;
  setCurrentIndexOfListedChart: (data?: any) => void;
  setCurrentIndexOfClinicianChart: (data?: any) => void;
  setPreviousViewScreen: (data?: any) => void;
  setPagination: (data?: any) => void;
}>({
  currentView: '',
  setCurrentView: () => { },
  setSelectedAuditData: () => { },
  setSelectedPromptData: () => { },
  setSelectedHarvestRunData: () => { },
  setCurrentPageHarvestRuns: () => { },
  sideBarItems: [],
  pagination: {},
  orgConfig: {},
  selectedBatchFileData: {},
  currentIndexOfListedChart: {},
  currentIndexOfClinicianChart: {},
  previousViewScreen: '',
  isCollapsed: false,
  setIsCollapsed: () => { },
  setSelectedBatchFileData: () => { },
  selectedExtractionRunData: {},
  setSelectedExtractionRunData: () => { },
  setCurrentIndexOfListedChart: () => { },
  setCurrentIndexOfClinicianChart: () => { },
  setPreviousViewScreen: () => { },
  setPagination: () => { }

});

// function DashboardView() {

//   const [dateRange, setDateRange] = useState<DateRange>({
//     from: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000), // Last 6 months
//     to: new Date(),
//   });

//   const [harvestRuns, setHarvestRuns] = useState([]);
//   const { user, token } = useAuth();
//   const [currentPage, setCurrentPage] = useState(1);
//   const [pagination, setPagination] = useState<any>(null);
//   // const isAdmin = user?.role === "ADMIN" ? true : false

//   // useEffect(() => {
//   //   const fetchHarvestRuns = async () => {
//   //     try {
//   //       const response = await fetch(`${import.meta.env.VITE_API_URL}/harvest/runs?page=${currentPage}`, {
//   //         method: 'GET',
//   //         cache: 'no-store',
//   //         headers: {
//   //           'Content-Type': 'application/json',
//   //           'Authorization': `Bearer ${token}`
//   //         }
//   //       });
//   //       const data = await response.json();

//   //       setHarvestRuns(data?.data?.harvestRuns || []);
//   //       setPagination(data?.data?.pagination || null) // Update this line based on actual response shape

//   //     } catch (error) {
//   //       console.error('Error fetching harvest runs:', error);
//   //     }
//   //   };

//   //   fetchHarvestRuns();
//   // }, [currentPage, token]);


//   const fetchData = async () => {
//     try {
//       const response = await apiClient('/harvest/runs', {
//         method: 'GET',
//         token,
//         queryParams: { page: currentPage },
//       });

//       // const DropdownResponse = await apiClient(`harvest/runs/type `, {
//       //   method: 'GET',
//       //   token,
//       // });


//       const data = await response.json();
//       setHarvestRuns(data?.data?.harvestRuns || []);
//       setPagination(data?.data?.pagination || null)

//       if (!response.ok) {
//         throw new Error(data.error || 'Request failed');
//       }
//       // if (DropdownResponse.ok) {
//       //   const Dddata = await DropdownResponse.json();
//       //   setDropdownVal(Dddata?.data?.harvestRunType ? Dddata?.data?.harvestRunType : {})
//       // }
//       // // const newData = await generateMockHarvestData(data)
//       // setHarvestRuns(data?.data?.harvestRuns)
//       // setPaginationData(data?.data?.pagination)
//       return data;
//     } catch (error: any) {
//       console.error('Fetch error:', error.message);
//       return null;
//     }
//   };

//   useEffect(() => {
//     fetchData()
//   }, [currentPage])





//   // Filter data based on selected date range
//   const filteredData = useMemo(() => {
//     if (!dateRange.from || !dateRange.to) return allAuditData;

//     return allAuditData.filter(audit =>
//       isWithinInterval(audit.date, {
//         start: startOfDay(dateRange.from!),
//         end: endOfDay(dateRange.to!),
//       })
//     );
//   }, [dateRange]);

//   // Calculate previous period data for comparison
//   const previousPeriodData = useMemo(() => {
//     if (!dateRange.from || !dateRange.to) return [];

//     const periodDuration = differenceInDays(dateRange.to, dateRange.from);
//     const previousPeriodEnd = subDays(dateRange.from, 1);
//     const previousPeriodStart = subDays(previousPeriodEnd, periodDuration);

//     return allAuditData.filter(audit =>
//       isWithinInterval(audit.date, {
//         start: startOfDay(previousPeriodStart),
//         end: endOfDay(previousPeriodEnd),
//       })
//     );
//   }, [dateRange]);

//   // Calculate metrics from filtered data
//   const metrics = useMemo(() => {
//     const totalAudits = filteredData.length;
//     const averageReviewTime = totalAudits > 0
//       ? filteredData.reduce((sum, audit) => sum + audit.reviewTime, 0) / totalAudits
//       : 0;

//     // Calculate AI savings
//     const aiAssistedAudits = filteredData.filter(audit => audit.isAiAssisted);
//     const actualTimeSpent = aiAssistedAudits.reduce((sum, audit) => sum + audit.reviewTime, 0);
//     const estimatedManualTime = aiAssistedAudits.reduce((sum, audit) => sum + audit.estimatedManualTime, 0);
//     const hoursSaved = estimatedManualTime - actualTimeSpent;

//     // Previous period metrics
//     const previousTotalAudits = previousPeriodData.length;
//     const previousAverageReviewTime = previousTotalAudits > 0
//       ? previousPeriodData.reduce((sum, audit) => sum + audit.reviewTime, 0) / previousTotalAudits
//       : 0;

//     // Previous period AI savings
//     const previousAiAssistedAudits = previousPeriodData.filter(audit => audit.isAiAssisted);
//     const previousActualTimeSpent = previousAiAssistedAudits.reduce((sum, audit) => sum + audit.reviewTime, 0);
//     const previousEstimatedManualTime = previousAiAssistedAudits.reduce((sum, audit) => sum + audit.estimatedManualTime, 0);
//     const previousHoursSaved = previousEstimatedManualTime - previousActualTimeSpent;

//     // Calculate percentage changes
//     const auditsTrend = {
//       percentage: previousTotalAudits > 0
//         ? ((totalAudits - previousTotalAudits) / previousTotalAudits) * 100
//         : 0,
//       isPositive: totalAudits >= previousTotalAudits,
//       hasData: previousTotalAudits > 0,
//     };

//     const reviewTimeTrend = {
//       percentage: previousAverageReviewTime > 0
//         ? ((averageReviewTime - previousAverageReviewTime) / previousAverageReviewTime) * 100
//         : 0,
//       isPositive: averageReviewTime < previousAverageReviewTime,
//       hasData: previousAverageReviewTime > 0,
//     };

//     const aiSavingsTrend = {
//       percentage: previousHoursSaved > 0
//         ? ((hoursSaved - previousHoursSaved) / previousHoursSaved) * 100
//         : 0,
//       isPositive: hoursSaved >= previousHoursSaved,
//       hasData: previousHoursSaved > 0,
//     };

//     return {
//       totalAudits,
//       averageReviewTime: averageReviewTime.toFixed(1),
//       hoursSaved: hoursSaved.toFixed(0),
//       auditsTrend,
//       reviewTimeTrend,
//       aiSavingsTrend,
//     };
//   }, [filteredData, previousPeriodData]);

//   // Prepare line chart data
//   const lineChartData = useMemo(() => {
//     const monthlyData = new Map();

//     filteredData.forEach(audit => {
//       const monthKey = format(audit.date, 'MMM yyyy');
//       if (!monthlyData.has(monthKey)) {
//         monthlyData.set(monthKey, { month: monthKey, audits: 0, date: audit.date });
//       }
//       monthlyData.get(monthKey).audits++;
//     });

//     return Array.from(monthlyData.values())
//       .sort((a, b) => a.date.getTime() - b.date.getTime())
//       .map(({ month, audits }) => ({ month, audits }));
//   }, [filteredData]);

//   // Prepare pie chart data
//   const pieChartData = useMemo(() => {
//     const outcomeCount = filteredData.reduce((acc, audit) => {
//       acc[audit.outcome] = (acc[audit.outcome] || 0) + 1;
//       return acc;
//     }, {} as Record<string, number>);

//     return [
//       { name: 'Approved', value: outcomeCount.Approved || 0, color: '#059669' },
//       { name: 'Rejected', value: outcomeCount.Rejected || 0, color: '#DC2626' },
//       { name: 'Skipped', value: outcomeCount.Skipped || 0, color: '#6B7280' },
//     ].filter(item => item.value > 0);
//   }, [filteredData]);



//   return (

//     <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-white">
//       <div className="p-8">
//         {/* Breadcrumb Navigation */}
//         <div className="mb-6">
//           <Breadcrumb>
//             <BreadcrumbList>
//               <BreadcrumbItem>
//                 <BreadcrumbPage className="flex items-center gap-2 text-base">
//                   <Home className="w-4 h-4" />
//                   Dashboard
//                 </BreadcrumbPage>
//               </BreadcrumbItem>
//             </BreadcrumbList>
//           </Breadcrumb>
//         </div>

//         {/* Enhanced Header */}
//         <div className="mb-8">
//           <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
//             <div>
//               <h1 className="text-3xl font-bold text-gray-900 mb-2 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text tracking-tight leading-tight">
//                 Healthcare Audit Dashboard
//               </h1>
//               <p className="text-base text-gray-600 max-w-2xl leading-relaxed">
//                 Monitor audit activities, track performance metrics, and analyze compliance trends across your healthcare organization
//               </p>
//             </div>

//             {/* Date Range Filter Card */}
//             <Card className="bg-white/80 backdrop-blur-sm border-gray-200 shadow-lg">
//               <CardContent className="p-4">
//                 <div className="flex items-center gap-2 mb-2">
//                   <Calendar className="w-4 h-4 text-gray-500" />
//                   <span className="text-sm font-medium text-gray-700">Date Range</span>
//                 </div>
//                 <DateRangeFilter
//                   dateRange={dateRange}
//                   onDateRangeChange={setDateRange}
//                 />
//                 {dateRange.from && dateRange.to && (
//                   <p className="text-xs text-muted-foreground mt-2">
//                     {format(dateRange.from, 'MMM dd, yyyy')} - {format(dateRange.to, 'MMM dd, yyyy')}
//                     <Badge variant="secondary" className="ml-2 text-xs">
//                       {filteredData.length} records
//                     </Badge>
//                   </p>
//                 )}
//               </CardContent>
//             </Card>
//           </div>
//         </div>

//         {/* Enhanced Metric Cards */}
//         <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
//           <MetricCard
//             title="Audits Completed"
//             value={metrics.totalAudits.toLocaleString()}
//             icon={CheckCircle}
//             trend={metrics.auditsTrend}
//             description="Total audit reviews completed in selected period"
//           />
//           <MetricCard
//             title="Average Review Time"
//             value={`${metrics.averageReviewTime} hrs`}
//             icon={Clock}
//             trend={metrics.reviewTimeTrend}
//             description="Mean time spent per audit review"
//           />
//           <MetricCard
//             title="AI Time Savings"
//             value={`${metrics.hoursSaved} hrs`}
//             icon={Zap}
//             trend={metrics.aiSavingsTrend}
//             description="Time saved through AI-assisted reviews"
//           />
//         </div>

//         {/* Enhanced Charts Section */}
//         {(lineChartData.length > 0 || pieChartData.length > 0) && (
//           <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
//             {/* Line Chart - Spans 2 columns */}
//             {lineChartData.length > 0 && (
//               <Card className="lg:col-span-2 bg-white/80 backdrop-blur-sm border-gray-200 shadow-lg">
//                 <CardHeader className="pb-4">
//                   <CardTitle className="flex items-center gap-2 text-xl font-semibold">
//                     <TrendingUp className="w-5 h-5 text-blue-600" />
//                     Audit Volume Trends
//                   </CardTitle>
//                   <CardDescription className="text-sm text-muted-foreground">
//                     Monthly audit completion trends over the selected period
//                   </CardDescription>
//                 </CardHeader>
//                 <CardContent>
//                   <div style={{ height: '320px' }}>
//                     <ResponsiveContainer width="100%" height="100%">
//                       <LineChart data={lineChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
//                         <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" opacity={0.6} />
//                         <XAxis
//                           dataKey="month"
//                           stroke="#6B7280"
//                           fontSize={11}
//                           tickLine={false}
//                           axisLine={false}
//                         />
//                         <YAxis
//                           stroke="#6B7280"
//                           fontSize={11}
//                           tickLine={false}
//                           axisLine={false}
//                         />
//                         <Tooltip
//                           contentStyle={{
//                             backgroundColor: 'white',
//                             border: '1px solid #E5E7EB',
//                             borderRadius: '12px',
//                             boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
//                             fontSize: '12px',
//                           }}
//                           labelStyle={{ color: '#374151', fontWeight: 500 }}
//                         />
//                         <Line
//                           type="monotone"
//                           dataKey="audits"
//                           stroke="#3B82F6"
//                           strokeWidth={3}
//                           dot={{ fill: '#3B82F6', strokeWidth: 2, r: 6 }}
//                           activeDot={{ r: 8, stroke: '#3B82F6', strokeWidth: 2, fill: '#EFF6FF' }}
//                         />
//                       </LineChart>
//                     </ResponsiveContainer>
//                   </div>
//                 </CardContent>
//               </Card>
//             )}

//             {/* Pie Chart - 1 column */}
//             {pieChartData.length > 0 && (
//               <Card className="bg-white/80 backdrop-blur-sm border-gray-200 shadow-lg">
//                 <CardHeader className="pb-4">
//                   <CardTitle className="flex items-center gap-2 text-xl font-semibold">
//                     <Activity className="w-5 h-5 text-green-600" />
//                     Decision Outcomes
//                   </CardTitle>
//                   <CardDescription className="text-sm text-muted-foreground">
//                     Distribution of audit decisions
//                   </CardDescription>
//                 </CardHeader>
//                 <CardContent>
//                   <div style={{ height: '320px' }}>
//                     <ResponsiveContainer width="100%" height="100%">
//                       <PieChart>
//                         <Pie
//                           data={pieChartData}
//                           cx="50%"
//                           cy="45%"
//                           labelLine={false}
//                           label={({ name, percent }) => `${name}\n${(percent * 100).toFixed(0)}%`}
//                           outerRadius={80}
//                           fill="#8884d8"
//                           dataKey="value"
//                           style={{ fontSize: '11px' }}
//                         >
//                           {pieChartData.map((entry, index) => (
//                             <Cell key={`cell-${index}`} fill={entry.color} />
//                           ))}
//                         </Pie>
//                         <Tooltip
//                           contentStyle={{
//                             backgroundColor: 'white',
//                             border: '1px solid #E5E7EB',
//                             borderRadius: '12px',
//                             boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
//                             fontSize: '12px',
//                           }}
//                         />
//                         <Legend
//                           verticalAlign="bottom"
//                           align="center"
//                           layout="horizontal"
//                           iconType="circle"
//                           wrapperStyle={{ paddingTop: '20px', fontSize: '11px' }}
//                         />
//                       </PieChart>
//                     </ResponsiveContainer>
//                   </div>
//                 </CardContent>
//               </Card>
//             )}
//           </div>
//         )}

//         {/* Recent Harvest Runs Table */}
//         <div className="mb-8">
//           <HarvestRunTable
//             harvestRuns={harvestRuns || []}
//             pagination={pagination}
//             currentPage={currentPage}
//             onPageChange={(page) => setCurrentPage(page)}
//             limit={10}
//           />
//         </div>


//         {/* Enhanced No Data Message */}
//         {/* {filteredData.length === 0 && (
//           <Card className="bg-white/80 backdrop-blur-sm border-gray-200 shadow-lg">
//             <CardContent className="text-center py-16">
//               <div className="max-w-md mx-auto">
//                 <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
//                   <Activity className="w-8 h-8 text-gray-400" />
//                 </div>
//                 <h3 className="text-lg font-medium text-gray-900 mb-2">No audit data found</h3>
//                 <p className="text-base text-gray-600 mb-4 leading-relaxed">
//                   No audit records were found for the selected date range. Try adjusting your filters or selecting a different time period.
//                 </p>
//                 <Badge variant="outline" className="text-xs text-gray-500">
//                   {dateRange.from && dateRange.to && (
//                     `${format(dateRange.from, 'MMM dd, yyyy')} - ${format(dateRange.to, 'MMM dd, yyyy')}`
//                   )}
//                 </Badge>
//               </div>
//             </CardContent>
//           </Card>
//         )} */}
//       </div>
//     </div>
//   );
// }

// Placeholder views for unimplemented features
function PlaceholderView({ title, description }: { title: string; description: string }) {
  return (
    <Layout>
      <div className="p-8 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4 tracking-tight leading-tight">
          {title}
        </h1>
        <p className="text-base text-gray-600 mb-8 leading-relaxed">
          {description}
        </p>
        <div className="max-w-md mx-auto p-6 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-base text-blue-800">
            This feature is under development and will be available in a future release.
          </p>
        </div>
      </div>
    </Layout>
  );
}

const ProtectedLayout = ({ isAuthenticated }: { isAuthenticated: boolean }) => {
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.signIn} replace />;
  }
  // The Layout component wraps the child routes
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
};

// 2. Public Route: Only renders if NOT authenticated (e.g., Login page), else redirects to Dashboard
const PublicRoute = ({ isAuthenticated, children }: { isAuthenticated: boolean, children: React.ReactNode }) => {
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

// Main App Component with Authentication
function MainApp() {
  const { isAuthenticated, isLoading, token, user } = useAuth();
  const [authView, setAuthView] = useState<AuthView>('signin');
  const [resetPasswordView, setResetPasswordView] = useState<Boolean>(false);
  const [orgConfig, setOrgConfig] = useState<OrgType | null>();
  const navigate = useNavigate();

  const userRoles = user?.roles || [];
  const [appState, setAppState] = useState<AppState>({
    currentView: '',
  });

  const [currentPageHarvestRun, setCurrentPageHarvestRuns] = useState();
  const [sideBarItems, setSidebarItems] = useState<any>([]);
  const [currentIndexOfListedChart, setCurrentIndexOfListedChart] = useState(initialChartIndexState);
  const [currentIndexOfClinicianChart, setCurrentIndexOfClinicianChart] = useState(initialChartIndexState);
  const [previousViewScreen, setPreviousViewScreen] = useState<String>("");
  const [pagination, setPagination] = useState(paginationFields)

  const [isCollapsed, setIsCollapsed] = useState(true);
  useEffect(() => {
    async function getMenu() {
      try {
        const res = await apiClient('roles/side-menu', {
          method: 'GET',
          token,
        });
        const data = await res.json();
        let mapSideItems = mapSideMenu(data?.menu ?? [], appState.currentView);
        setSidebarItems(mapSideItems);
        const firstItemId = mapSideItems?.[0]?.id;
        if (firstItemId) {
          setCurrentView(firstItemId);
        }
        setOrgConfig(orgConfigDetails);
      } catch (error) {
        console.error("Error loading menu:", error);
      }

    }
    if (token) {
      getMenu();
    }

  }, [token]);

  useEffect(() => {
    const path = window.location.pathname;
    if (path.startsWith("/reset-password")) {
      setResetPasswordView(true);
      setAuthView("reset-password");
    }
  }, []);

  const getHomeRoute = (roles: string[]) => {
    // 1. Safety check: ensure roles exists and is an array
    if (!roles || !Array.isArray(roles)) return ROUTES.dashboard;
    console.log("roles", roles);

    // 2. Check if ANY role in the array contains "auditor" (case-insensitive)
    const isAuditor = roles.some(role =>
      role.toLowerCase().includes('auditor')
    );

    console.log("isAudtor", isAuditor);

    if (isAuditor) {
      return ROUTES.chartReviewList;
    }

    // 3. Check if ANY role contains "admin"
    const isAdmin = roles.some(role =>
      role.toLowerCase().includes('admin')
    );

    if (isAdmin) {
      return ROUTES.dashboard;
    }

    // 4. Default fallback
    return ROUTES.dashboard;
  };
  const setCurrentView = (view: AppView) => {
    setAppState(prev => ({ ...prev, currentView: view }));
  };

  const setSelectedAuditData = (data?: any) => {
    setAppState(prev => ({ ...prev, selectedAuditData: data }));
  };

  const setSelectedPromptData = (data?: any) => {
    setAppState(prev => ({ ...prev, selectedPromptData: data }));
  };

  const setSelectedHarvestRunData = (data?: any) => {
    setAppState(prev => ({ ...prev, selectedHarvestRunData: data }));
  };
  const setSelectedBatchFileData = (data?: any) => {
    setAppState(prev => ({ ...prev, selectedBatchFileData: data }));
  };
  const setSelectedExtractionRunData = (data?: any) => {
    setAppState(prev => ({ ...prev, selectedExtractionRunData: data }));
  }

  // Keep AppContext.currentView in sync with the URL so existing consumers
  // still work while we move to full route-based navigation.
  const location = useLocation();
  useEffect(() => {
    const viewFromPath = PATH_TO_VIEW[location.pathname];
    if (viewFromPath && viewFromPath !== appState.currentView) {
      setCurrentView(viewFromPath as AppView);
    }
  }, [location.pathname]);

  // Show loading screen while checking authentication
  // if (isLoading) {
  //   return (
  //     <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-gray-50 flex items-center justify-center">
  //       <div className="text-center">
  //         <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-xl mb-4 animate-pulse">
  //           <Activity className="w-8 h-8 text-white" />
  //         </div>
  //         <p className="text-base text-gray-600">Loading...</p>
  //       </div>
  //     </div>
  //   );
  // }

  // Show authentication views if not authenticated
  if (!isAuthenticated || resetPasswordView) {
    return (
      <>
        {authView === 'signin' && (
          <SignIn onResetPassword={() => setAuthView('reset-password')} />
        )}
        {authView === 'reset-password' && (
          <ResetPasswordPage
            onBackToSignIn={() => {
              setAuthView('signin');
              setResetPasswordView(false);
              window.location.href = '/signin';
            }}
          />
        )}
      </>
    );
  }

  // Show main application if authenticated
  return (
    <AppContext.Provider value={{
      currentView: appState.currentView,
      selectedAuditData: appState.selectedAuditData,
      selectedPromptData: appState.selectedPromptData,
      selectedHarvestRunData: appState.selectedHarvestRunData,
      currentPageHarvestRun: currentPageHarvestRun,
      isCollapsed,
      orgConfig,
      selectedBatchFileData: appState.selectedBatchFileData,
      sideBarItems,
      pagination,
      currentIndexOfListedChart: currentIndexOfListedChart,
      currentIndexOfClinicianChart: currentIndexOfClinicianChart,
      previousViewScreen: previousViewScreen,
      selectedExtractionRunData: appState.selectedExtractionRunData,
      setSelectedExtractionRunData,
      setIsCollapsed,
      setCurrentView,
      setSelectedAuditData,
      setSelectedPromptData,
      setSelectedHarvestRunData,
      setCurrentPageHarvestRuns,
      setSelectedBatchFileData,
      setCurrentIndexOfListedChart,
      setCurrentIndexOfClinicianChart,
      setPreviousViewScreen,
      setPagination
    }}>
      {/* <Layout> */}
      <Routes>
        {/* Simple pages */}
        {/* --- Public Routes (Sign In / Reset Password) --- */}
        <Route
          path={ROUTES.signIn}
          element={
            <PublicRoute isAuthenticated={isAuthenticated}>
              <SignIn
                onResetPassword={() => navigate(ROUTES.resetPassword)}
              />
            </PublicRoute>
          }
        />

        <Route
          path={ROUTES.resetPassword}
          element={
            <PublicRoute isAuthenticated={isAuthenticated}>
              <ResetPasswordPage
                onBackToSignIn={() => {
                  navigate(ROUTES.signIn)
                  setResetPasswordView(false)
                }
                }
              />
            </PublicRoute>
          }
        />

        {/* Magic Link Handler - Public route (no auth required) */}
        <Route
          path={ROUTES.magic}
          element={<MagicLinkHandler />}
        />
        <Route element={<ProtectedLayout isAuthenticated={isAuthenticated} />}>
          <Route path="/" element={<Navigate to={getHomeRoute(userRoles)} replace />} />
          <Route path={ROUTES.dashboard} element={<DashboardView />} />
          <Route path={ROUTES.turfAiStudio} element={<TurfAIStudio />} />
          <Route path={ROUTES.activityLogs} element={<SystemAuditLog />} />
          <Route path={ROUTES.backupHistory} element={<BackUpManagement />} />
          <Route path={ROUTES.userManagement} element={<UserManagement />} />
          <Route path={ROUTES.settings} element={<Settings />} />

          {/* Chart review */}
          <Route path={ROUTES.chartReviewList} element={<ChatHarvest />} />
          <Route path={ROUTES.chartReviewRun} element={<HarvestRunView />} />
          <Route path={ROUTES.auditDetail} element={<AuditGoalsDetailView />} />
          <Route path={ROUTES.snChartReview} element={<RNChartDetailView />} />

          {/* Chart extraction */}
          <Route path={ROUTES.chartExtractionList} element={<ChartExtractionRuns />} />
          <Route path={ROUTES.chartExtractionDetail} element={<ChartExtractionRunDetail />} />

          {/* Prompts */}
          <Route path={ROUTES.promptsList} element={<AIPromptManager />} />
          <Route path={ROUTES.promptCreate} element={<PromptDetailView />} />
          <Route path={ROUTES.promptDetail} element={<PromptDetailView />} />

          {/* Client overview */}
          <Route path={ROUTES.clientOverviewList} element={<ClientClinicianView />} />

          {/* KanTime */}
          <Route path={ROUTES.kanTimeStatus} element={<KanTimeDetails />} />
          <Route path={ROUTES.kanTimeLog} element={<KanTimeRunLogView />} />
          <Route path={ROUTES.kanTimeUpdate()} element={<KanTimeUpdate />} />
          <Route path={ROUTES.profile} element={<Profile />} />

          {/* Renewal Tasks */}
          <Route path={ROUTES.renewalTasks} element={<RenewalTasksDashboard viewMode="my-tasks" />} />
          <Route path={ROUTES.supervisorDashboard} element={<SupervisorDashboard />} />
          <Route path={ROUTES.supervisorRiskView} element={<SupervisorRiskView />} />
          <Route path={ROUTES.staffTasksView(':staffId')} element={<StaffTasksView />} />
          <Route path={ROUTES.clientDetailsView(':clientId')} element={<ClientDetailsView />} />
          <Route path={ROUTES.workflowRunLogs} element={<WorkflowRunLogsView />} />

          {/* Other existing views */}
          <Route path="/batch-approval" element={<BatchApproval />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/admin-audit-dashboard" element={<AdminAuditDashboard />} />
          <Route path="/system-activity-log" element={<SystemActivityLog />} />
          <Route path="/user-activity-tracker" element={<UserActivityTracker />} />
          <Route path="/security-audit-log" element={<SecurityAuditLog />} />
          <Route path="/performance-monitor" element={<PerformanceMonitor />} />
          <Route path="/configuration-audit" element={<ConfigurationAudit />} />
          <Route path="/batch-run-log-view" element={<HarvestRunLogView />} />
          <Route path={ROUTES.chartReviewLog()} element={<HarvestRunLogView />} />
          <Route path="/audit-decision-comparison" element={<AuditDecisionComparison />} />
          <Route path="/batch-files" element={<BatchFileManager />} />
          <Route path="/batch-files/:batchFileId" element={<BatchFileDetailView />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to={getHomeRoute(userRoles)} replace />} />
        </Route>
      </Routes>
      {/* </Layout> */}
    </AppContext.Provider>
  );
}

// Root App Component with Authentication Provider
export default function App() {

  return (
    <AuthProvider>
      <MainApp />
      <Toaster />
    </AuthProvider>
  );
}